import axiosWrapper from "@AxiosWrapper";
import { getBackendUrl } from "@Utils";
import type {
  CreateExternalLinkRequest,
  CreateExternalLinkResponse,
  ExternalLinkListItem,
  GetExternalLinkCountsResponse,
  GetExternalLinkMoveImpactResponse,
  GetExternalLinkStatsResponse,
  GetExternalLinksForResourceResponse,
  GetPortalExternalLinksRequest,
  GetPortalExternalLinksResponse,
  ReinstateExternalLinkRequest,
  ReinstateExternalLinkResponse,
  RevokeExternalLinkResponse,
  UpdateExternalLinkRequest,
  SetExternalLinkDisabledResponse,
  UpdateExternalLinkResponse,
} from "@ServerTypes/externalLinks/externalLinks.schema";

/**
 * Thin API client for the AUTHENTICATED external-links routes (CP-283). Every response type is imported
 * from the server's own Zod schemas rather than re-declared here, so a field added or renamed on the
 * server surfaces as a compile error in the components instead of as `undefined` at runtime.
 *
 * The recipient-facing routes are deliberately NOT here — they live in
 * `features/external-share/sharedLink.api.ts` and use a bare axios client with no session plumbing.
 */

/**
 * `permission` values accepted by create/update, taken from the server's own request schema rather
 * than restated — a third permission tier added there becomes a compile error here instead of a
 * silently unhandled value.
 */
export type ExternalLinkPermission = CreateExternalLinkRequest["permission"];

/** The resource types a link may be created on, from the server's own request schema. */
export type ExternalLinkShareableResourceType = CreateExternalLinkRequest["resourceType"];

export type { ExternalLinkListItem };

const base = () => `${getBackendUrl()}/externalLinks`;

/**
 * The create payload IS the server's request type — no parallel declaration to drift.
 *
 * Note `expiresOn` (a bare `YYYY-MM-DD`) rather than `expiresAt`: the server resolves the date to the
 * end of that day in the PORTAL's timezone. Computing the instant in the browser would use the
 * creator's own zone, so a creator outside the project's zone would set an expiry the recipient reads
 * as a different day.
 */
export type CreateExternalLinkPayload = CreateExternalLinkRequest;

export async function createExternalLink(payload: CreateExternalLinkPayload) {
  const res = await axiosWrapper.post<CreateExternalLinkResponse, CreateExternalLinkPayload>(
    `${base()}/createExternalLink`,
    payload,
  );
  return res.data;
}

/**
 * Fields omitted from the payload are left untouched server-side; `expiresAt: null` / `password: null`
 * explicitly CLEAR them. Callers must therefore build this object by presence, not by spreading
 * defaults — spreading `{ password: undefined }` reads as "leave alone", which is usually right, but
 * spreading `{ password: null }` wipes a password the user never touched.
 */
export type UpdateExternalLinkPayload = UpdateExternalLinkRequest;

export async function updateExternalLink(payload: UpdateExternalLinkPayload) {
  const res = await axiosWrapper.post<UpdateExternalLinkResponse, UpdateExternalLinkPayload>(
    `${base()}/updateExternalLink`,
    payload,
  );
  return res.data;
}

export async function revokeExternalLink(linkId: string) {
  const res = await axiosWrapper.post<RevokeExternalLinkResponse>(`${base()}/revokeExternalLink`, { linkId });
  return res.data;
}

/**
 * "Enable Link" on the Revoked tab — brings a revoked link back with its ORIGINAL URL.
 *
 * When the link's stored expiry has already passed the server answers `{ status: true,
 * expiryRequired: true }` and changes nothing: callers must then re-send with `expiresOn`. That is a
 * successful response, not an error, so a caller that only checks `status` will silently do nothing.
 */
export async function reinstateExternalLink(payload: ReinstateExternalLinkRequest) {
  const res = await axiosWrapper.post<ReinstateExternalLinkResponse, ReinstateExternalLinkRequest>(
    `${base()}/reinstateExternalLink`,
    payload,
  );
  return res.data;
}

export async function getExternalLinksForResource(params: {
  resourceId: string;
  spaceId: string;
  includeRevoked?: boolean;
}) {
  const res = await axiosWrapper.post<GetExternalLinksForResourceResponse>(
    `${base()}/getExternalLinksForResource`,
    params,
  );
  return res.data;
}

/**
 * Active-link counts for the grid's link badge, keyed by resource id.
 *
 * The server returns a SPARSE map — resources with no links are simply absent — so callers must
 * treat a missing key as zero rather than expecting an entry per requested id.
 */
export async function getExternalLinkCounts(resourceIds: string[]) {
  const res = await axiosWrapper.post<GetExternalLinkCountsResponse>(`${base()}/getExternalLinkCounts`, {
    resourceIds,
  });
  return res.data;
}

export async function getExternalLinkStats(params: { linkId: string; types?: string[]; skip?: number; take?: number }) {
  const res = await axiosWrapper.post<GetExternalLinkStatsResponse>(`${base()}/getExternalLinkStats`, params);
  return res.data;
}

/**
 * How many active links a cut/paste would break. Only counts links ON the moved items themselves —
 * moving something into or out of a linked folder is not a break and must not warn.
 */
export async function getExternalLinkMoveImpact(resourceIds: string[]) {
  const res = await axiosWrapper.post<GetExternalLinkMoveImpactResponse>(`${base()}/getExternalLinkMoveImpact`, {
    resourceIds,
  });
  return res.data;
}

/**
 * Params are the server's own request type rather than a hand-listed subset — that is what makes the
 * register's `where` search clause and `sorted` config type-checked against the server's field
 * allow-list instead of accepted as free-form strings.
 */
export async function getPortalExternalLinks(params: GetPortalExternalLinksRequest) {
  const res = await axiosWrapper.post<GetPortalExternalLinksResponse, GetPortalExternalLinksRequest>(
    `${base()}/getPortalExternalLinks`,
    params,
  );
  return res.data;
}

export async function setExternalLinkDisabled(linkId: string, disabled: boolean) {
  const res = await axiosWrapper.post<SetExternalLinkDisabledResponse>(`${base()}/setExternalLinkDisabled`, {
    linkId,
    disabled,
  });
  return res.data;
}
